import { useState, useRef, useEffect } from 'react';
import useSWR, { mutate, SWRConfiguration } from 'swr';

// mutateが発生したかどうかをグローバルに管理するフラグ
let hasMutated = false;

export function useSWRWithLoadingControl<T>(
  key: string | [string, ...unknown[]] | null | undefined,
  fetcher: (url: string) => Promise<T>,
  options?: SWRConfiguration
) {
  const mergedOptions: SWRConfiguration = {
    dedupingInterval: 30000, // 同じキーでの重複リクエストを防ぐ間隔（10秒）
    revalidateOnFocus: false, // ウィンドウがフォーカスされた時に自動的に再検証
    revalidateOnReconnect: false, // ネットワーク接続が復帰した時に自動的に再検証しない
    keepPreviousData: true, // 新しいデータを取得中も古いデータを保持する
    shouldRetryOnError: false, // エラー時のリトライはしない
    ...options
  };

  const swr = useSWR<T>(key, fetcher, mergedOptions);
  const [isLoading, setIsLoading] = useState(false);
  const loadingStartTime = useRef<number | null>(null);
  const minLoadingTime = 500;

  useEffect(() => {
    if (swr.isValidating && !hasMutated) {
      if (!isLoading) {
        loadingStartTime.current = performance.now();
        setIsLoading(true);
      }
    } else if (isLoading && !swr.isValidating) {
      const elapsedTime = performance.now() - (loadingStartTime.current ?? 0);
      const delay = Math.max(minLoadingTime - elapsedTime, 0);

      const timer = setTimeout(() => {
        setIsLoading(false);
      }, delay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [swr.isValidating, isLoading]);

  return {
    ...swr,
    data: swr.data,
    error: swr.error,
    isLoading: isLoading || (swr.isValidating && !hasMutated)
  };
}

// グローバルミューテーション関数
export function mutateWithoutLoading() {
  hasMutated = true; // ミューテーション時にローディングをスキップ
  const result = mutate(() => true); // keyなしで全体をミューテーション

  // 短い遅延を設けてhasMutatedをリセットし、他のSWRフックがisValidating状態を適切に処理できるようにする
  setTimeout(() => {
    hasMutated = false;
  }, 100);

  return result;
}
